import React, { useEffect, useState, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { getAllTickets, getChats, getMessages, changeTicketStatus, deleteChat } from "data/controllers/supportController";

function Support() {
  const [selectedTab, setSelectedTab] = useState(0);

  // Tickets State
  const [tickets, setTickets] = useState([]);
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketLimit] = useState(10);
  const [ticketTotalPages, setTicketTotalPages] = useState(1);

  // Chats State
  const [chats, setChats] = useState([]);
  const [chatPage, setChatPage] = useState(1);
  const [chatLimit] = useState(10);
  const [chatTotal, setChatTotal] = useState(0);
  const [chatTotalPages, setChatTotalPages] = useState(1);

  const [openDialog, setOpenDialog] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotalPages, setMessagesTotalPages] = useState(1);
  const scrollContainerRef = useRef(null);

  // Confirm dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("");
  const [selectedChatId, setSelectedChatId] = useState(null);

useEffect(() => {
  if (scrollContainerRef.current && openDialog) {
    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
  }
}, [openDialog]);

  const tabs = [
    { label: "All Tickets", value: 0 },
    { label: "All Chats", value: 1 },
  ];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (selectedTab === 0) {
      fetchTickets();
    } else {
      fetchChats();
    }
    // eslint-disable-next-line
  }, [selectedTab, chatPage, ticketPage]);

  const fetchTickets = async () => {
    let response;
    try {
       response = await getAllTickets(ticketPage, ticketLimit);
      const ticketsData = response.data.tickets || [];
      const mappedTickets = ticketsData.map((ticket) => ({
        id: ticket._id,
        userId: ticket.userId._id,
        name: ticket.userId?.personalDetails?.fullName || "Unknown",
        email: ticket.contacts?.email || "N/A",
        phone: ticket.contacts?.phone || "N/A",
        role: ticket.userId?.role || "candidate",
        profilePicture: ticket.userId?.personalDetails?.profilePicture || "/images/default-profile.png",
        companyLogo: ticket.userId?.companyDetails?.companyLogo || "/images/default-company.png",
        issue: ticket.issue,
        status: ticket.status,
        createdAt: ticket.createdAt,
      }));
      setTickets(mappedTickets);
      setTicketTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  const fetchChats = async () => {
    try {
      const response = await getChats(chatPage, chatLimit);
      const chatsData = response.data.chats || [];
      const mappedChats = chatsData.map((chat) => ({
        id: chat.userId,
        personName: chat.user?.personalDetails?.fullName || "Unknown",
        lastMessage: chat.lastMessage,
        imageUrl: chat.user?.role === "candidate"
          ? chat.user?.personalDetails?.profilePicture
          : chat.user?.companyDetails?.companyLogo,
      }));
      setChats(mappedChats);
      setChatTotal(response.data.totalChats || 0);
      setChatTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  const handleDeleteChat = (chatId) => {
    setDialogOpen(true);
    setDialogMode("delete");
    setSelectedChatId(chatId);
  };

  const confirmDeleteChat = async () => {
    try {
      await deleteChat(selectedChatId);
      fetchChats();
    } catch (error) {
      console.error("Error deleting chat:", error);
    } finally {
      setDialogOpen(false);
      setDialogMode("");
      setSelectedChatId(null);
    }
  };

  const handleViewMessages = async (userId, page = 1) => {
    try {
      const response = await getMessages(userId, page);
      setMessages(response.data.messages || []);
      setCurrentUserId(userId);
      setMessagesPage(response.data.page);
      setMessagesTotalPages(response.data.totalPages);
      setOpenDialog(true);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const handleLoadMoreMessages = async () => {
    const nextPage = messagesPage + 1;
    try {
      const response = await getMessages(currentUserId, nextPage);
      setMessages([...response.data.messages, ...messages]);
      setMessagesPage(response.data.page);
      setMessagesTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error loading more messages:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMessages([]);
    setMessageInput("");
    setCurrentUserId(null);
  };

  const handleChangeStatus = async (ticketId, newStatus) => {
    try {
      await changeTicketStatus(ticketId, newStatus);
      const updatedTickets = tickets.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
      );
      setTickets(updatedTickets);
    } catch (error) {
      console.error("Error updating ticket status:", error);
    }
  };

  const chatColumns = [
    {
      Header: "User",
      accessor: "user",
      align: "left",
      Cell: ({ row }) => {
        const { personName, imageUrl, lastMessage } = row.original;

        return (
          <MDBox display="flex" alignItems="center">
            <Box
              component="img"
              src={imageUrl}
              alt={personName}
              sx={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                objectFit: "cover",
                mr: 2,
              }}
            />
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
                {personName}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" sx={{ maxWidth: "800px", whiteSpace: "normal", wordBreak: "break-word" }}>
                {lastMessage}
              </MDTypography>
            </MDBox>
          </MDBox>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      Cell: ({ row }) => (
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDButton
            variant="outlined"
            size="small"
            color="info"
            onClick={() => handleViewMessages(row.original.id)}
          >
            View Messages
          </MDButton>
          <MDButton
            variant="outlined"
            size="small"
            color="error"
            onClick={() => handleDeleteChat(row.original.id)}
          >
            Delete Chat
          </MDButton>
        </MDBox>
      ),
    },
  ];

  const chatRows = chats.map((chat) => ({
    id: chat.id,
    personName: chat.personName,
    lastMessage: chat.lastMessage,
    imageUrl: chat.imageUrl,
  }));

  const renderTicketsTab = () => (
    <MDBox>
      <MDTypography variant="h6" mb={3}>
        All Support Tickets
      </MDTypography>
      <DataTable
        table={{
          columns: [
            {
              Header: "User",
              accessor: "user",
              align: "left",
              Cell: ({ row }) => {
                const { name, email, phone, role, profilePicture, companyLogo } = row.original;
                const imageUrl = role === "candidate" ? profilePicture : companyLogo;

                return (
                  <MDBox display="flex" alignItems="center">
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={name}
                      sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        mr: 2,
                      }}
                    />
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
                        {name}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {email}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {phone}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              },
            },
            {
              Header: "Issue",
              accessor: "issue",
              align: "left",
              Cell: ({ value }) => (
                <MDBox sx={{ maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word" }}>
                  {value}
                </MDBox>
              ),
            },
            { Header: "Created At", accessor: "createdAt", align: "center" },
            {
              Header: "Status",
              accessor: "status",
              align: "center",
              Cell: ({ row }) => (
                <Box sx={{ minWidth: 120 }}>
                  <Select
                    value={row.original.status}
                    onChange={(e) => handleChangeStatus(row.original.id, e.target.value)}
                    size="small"
                    displayEmpty
                    sx={{
                      color: row.original.status === "open" ? "success.main" : "error.main",
                      ".MuiSelect-icon": { color: "inherit" },
                    }}
                    renderValue={(selected) => (
                      <Box display="flex" alignItems="center">
                        <MDTypography variant="caption" sx={{ mr: 1 }}>
                          {selected === "open" ? "Open" : "Closed"}
                        </MDTypography>
                        <EditIcon sx={{ fontSize: "14px", opacity: 0.6 }} />
                      </Box>
                    )}
                  >
                    <MenuItem value="open" sx={{ color: "green" }}>Open</MenuItem>
                    <MenuItem value="closed" sx={{ color: "red" }}>Closed</MenuItem>
                  </Select>
                </Box>
              ),
            },
            {
              Header: "Actions",
              accessor: "actions",
              align: "center",
              Cell: ({ row }) => (
                <MDButton
                  variant="outlined"
                  size="small"
                  color="info"
                  onClick={() => handleViewMessages(row.original.userId)}
                >
                  View Messages
                </MDButton>
              ),
            },
          ],
          rows: tickets,
        }}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        noEndBorder
      />
      <MDBox display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={ticketTotalPages}
          page={ticketPage}
          onChange={(e, value) => setTicketPage(value)}
          color="primary"
        />
      </MDBox>
    </MDBox>
  );

  const renderChatsTab = () => (
    <MDBox>
      <DataTable
        table={{ columns: chatColumns, rows: chatRows }}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        noEndBorder
      />
      <MDBox display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={chatTotalPages}
          page={chatPage}
          onChange={(e, value) => setChatPage(value)}
          color="primary"
        />
      </MDBox>
    </MDBox>
  );

  return (
    <>
      {/* ConfirmDialog for delete chat */}
      {dialogOpen && dialogMode === "delete" && (
        <ConfirmDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={confirmDeleteChat}
          description="Are you sure you want to delete this chat?"
          confirmButtonText="Delete"
          confirmButtonColor="error"
        />
      )}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: 24,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Chat Messages
          <IconButton
            onClick={handleCloseDialog}
            sx={{ color: "black" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", height: "70vh", position: "relative", pb: 0 }}>
          <MDBox
            flex={1}
            display="flex"
            flexDirection="column"
            height="100%"
          >
            <MDBox
              ref={scrollContainerRef}
              flex={1}
              overflow="auto"
              display="flex"
              flexDirection="column-reverse"
              gap={1}
              px={1}
              pb={2}
            >
                {tickets.find(ticket => ticket.userId === currentUserId) && (
            <MDBox
              mt={1}
              width="100%"
              bgcolor="white"
              borderTop="1px solid #ddd"
              p={2}
              borderRadius="8px"
              boxShadow={1}
            >
              <MDTypography variant="subtitle2" mb={1}>
                Ticket Details
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Issue:</strong> {tickets.find(ticket => ticket.userId === currentUserId).issue}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Email:</strong> {tickets.find(ticket => ticket.userId === currentUserId).email}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Phone:</strong> {tickets.find(ticket => ticket.userId === currentUserId).phone}
              </MDTypography>
            </MDBox>
          )}
              {[...messages].reverse().map((msg, idx) => (
                <MDBox
                  key={idx}
                  alignSelf={msg.sender === "you" ? "flex-end" : "flex-start"}
                  bgcolor={msg.sender === "you" ? "white" : "grey.100"}
                  color="black"
                  px={2}
                  py={1.5}
                  borderRadius="12px"
                  border="1px solid #ddd"
                  maxWidth="75%"
                  boxShadow={msg.sender === "you" ? 1 : 0}
                >
                  <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {msg.content}
                  </MDTypography>
                  <MDTypography variant="caption" display="block" mt={0.5} textAlign="right" opacity={0.6}>
                    {msg.createdAt}
                  </MDTypography>
                </MDBox>
              ))}
              {messagesPage < messagesTotalPages && (
                <MDBox display="flex" justifyContent="center" mb={1}>
                  <MDButton
                    variant="outlined"
                    size="small"
                    color="info"
                    onClick={() => handleLoadMoreMessages()}
                  >
                    Load more messages...
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Support Management
                  </MDTypography>
                </MDBox>
                <MDBox pt={3} px={3}>
                  <Box sx={{ mb: 3 }}>
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      sx={{
                        "& .MuiTabs-indicator": {
                          height: "100%",
                          borderRadius: "8px",
                          backgroundColor: "white",
                        },
                        "& .MuiTab-root": {
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "14px",
                          minHeight: "40px",
                          borderRadius: "8px",
                          margin: "0 2px",
                          color: "#6c757d",
                          "&.Mui-selected": {
                            color: "#495057",
                            fontWeight: 600,
                          },
                        },
                      }}
                    >
                      {tabs.map((tab, index) => (
                        <Tab key={index} label={tab.label} />
                      ))}
                    </Tabs>
                  </Box>
                  {selectedTab === 0 && renderTicketsTab()}
                  {selectedTab === 1 && renderChatsTab()}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

export default Support;